import React, { useEffect, useMemo, useRef } from 'react';
import { createChart, AreaSeries, CandlestickSeries, LineSeries, HistogramSeries, ColorType } from 'lightweight-charts';

// lightweight-charts throws synchronously on invalid points (missing time, non-numeric OHLC),
// which would otherwise propagate out of this effect and unmount the whole React tree. Filtering
// bad points here means a malformed/mismatched data source just renders an empty chart instead of
// crashing the page.
function normalizeSeries(data = []) {
  return data
    .map((item) => ({
      time: item.time,
      open: item.open,
      high: item.high,
      low: item.low,
      close: item.close,
      value: item.close,
      volume: item.volume,
    }))
    .filter((item) => Number.isFinite(item.time) && Number.isFinite(item.close));
}

export default function PriceChart({ data, tall = false, chartType = 'candles', showVolume = true }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);

  const normalizedData = useMemo(() => normalizeSeries(data || []), [data]);

  useEffect(() => {
    if (!containerRef.current) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: 'rgba(255,255,255,0.62)',
        fontFamily: '-apple-system, "SF Pro Display", "Segoe UI", sans-serif',
      },
      grid: {
        vertLines: { color: 'rgba(255,255,255,0.06)' },
        horzLines: { color: 'rgba(255,255,255,0.06)' },
      },
      rightPriceScale: { borderColor: 'rgba(255,255,255,0.08)' },
      timeScale: { borderColor: 'rgba(255,255,255,0.08)' },
      crosshair: { mode: 0 },
      autoSize: true,
    });

    chartRef.current = chart;

    return () => {
      chart.remove();
      chartRef.current = null;
      seriesRef.current = null;
      volumeSeriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current || !normalizedData.length) return;

    if (seriesRef.current) {
      chartRef.current.removeSeries(seriesRef.current);
    }
    if (volumeSeriesRef.current) {
      chartRef.current.removeSeries(volumeSeriesRef.current);
    }

    // Candles need all four OHLC fields to be real numbers — data that's only a close/value
    // series (e.g. an equity curve) can't render as candles even if chartType says so.
    const hasFullOhlc = normalizedData.every(
      (item) => Number.isFinite(item.open) && Number.isFinite(item.high) && Number.isFinite(item.low)
    );
    const effectiveType = chartType === 'candles' && !hasFullOhlc ? 'area' : chartType;

    let series;
    if (effectiveType === 'line') {
      series = chartRef.current.addSeries(LineSeries, {
        color: '#5eead4',
        lineWidth: 2,
        priceLineVisible: false,
      });
    } else if (effectiveType === 'area') {
      series = chartRef.current.addSeries(AreaSeries, {
        lineColor: '#3b82f6',
        topColor: 'rgba(59, 130, 246, 0.22)',
        bottomColor: 'rgba(59, 130, 246, 0)',
        lineWidth: 2,
        priceLineVisible: false,
      });
    } else {
      series = chartRef.current.addSeries(CandlestickSeries, {
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
      });
    }

    seriesRef.current = series;

    try {
      series.setData(
        normalizedData.map((item) => (effectiveType === 'candles' ? item : { time: item.time, value: item.close }))
      );

      if (showVolume && hasFullOhlc) {
        const volumeSeries = chartRef.current.addSeries(HistogramSeries, {
          color: 'rgba(59, 130, 246, 0.24)',
          priceFormat: { type: 'volume' },
          priceScaleId: '',
        });
        volumeSeriesRef.current = volumeSeries;
        volumeSeries.setData(
          normalizedData.map((item) => ({
            time: item.time,
            value: item.volume || 0,
            color: item.close >= item.open ? 'rgba(34,197,94,0.32)' : 'rgba(239,68,68,0.32)',
          }))
        );
      }

      chartRef.current.timeScale().fitContent();
    } catch (err) {
      // Defense in depth: lightweight-charts throws synchronously on bad data. Never let that
      // escape this effect and unmount the surrounding page — log and show an empty chart instead.
      console.error('PriceChart: failed to render series', err);
    }
  }, [chartType, normalizedData, showVolume]);

  return <div ref={containerRef} className={`chart-container ${tall ? 'tall' : ''}`} />;
}
